import {constants} from './constants.js';
import * as country from './country.js';
import * as helpers from './helpers.js';
import {t} from './i18n.js';
import * as server from './server.js';
import * as state from './state.js';
import * as tax from './tax.js';

let productData = {};
let productDataCurrency = '';

async function init() {
  const countryIso = state.getCountryIso();
  if (countryIso === null) {
    const currency = state.getCurrency();
    state.setCountryIso(getDefaultCountryIsoByCurrency(currency));
  }
  await refreshProductData();
  updateTaxDisplay();
  if (state.getCurrency() == 'EUR' || state.getCurrency() == 'GBP') {
    /// properly sets tax-exempt to false for GBP, but saves the VAT number
    updateCustomerTaxExemptStatus();
  }
  checkForCountryCurrencyMismatch();
  if (state.getCurrency() == 'EUR') {
    zeroTaxesUpdate();
  }
  updateCheckoutCountry();
  updateProductsLink();
  updateAccessoriesLink();
  updateProductShipInfo();
  addHolidayShippingSchedule();
  preventPoBoxes();
  warnOnTooManyCharacters();
}

async function refreshProductData() {
  const currency = state.getCurrency();

  if (currency === productDataCurrency) return;  // no need to update

  /*
      productUrlsByCurrency: {
          AUD: 'https://aud.saleae.com/pages/products-json',
          CAD: 'https://cad.saleae.com/pages/products-json',
          CNY: 'https://cny.saleae.com/pages/products-json',
          EUR: 'https://eur.saleae.com/pages/products-json',
          GBP: 'https://gbp.saleae.com/pages/products-json',
          JPY: 'https://jpy.saleae.com/pages/products-json',
          TWD: 'https://ntd.saleae.com/pages/products-json',
          USD: 'https://usd.saleae.com/pages/products-json',
      },
  */
  // I've hard-coded Shopify data in constants at least for now.
  // ideally the pages above all get cached by fastly or a lambda instance
  productData = constants.productDataByCurrency[currency];

  // const url = constants.productUrlsByCurrency[currency];
  // let response = await helpers.fetchRetry(url,{},4);
  // productData = await response.json();

  productDataCurrency = currency;
}

// returns null if variant cannot be found for this SKU
function getVariantBySku(sku) {
  if (typeof productData[sku] === 'undefined') {
    console.warn(`SKU ${sku} not found in productData:`);

    return null;
  }
  return productData[sku].id;
}

// returns undefined if not found
function getSkuByVariant(variant) {
  let sku = Object.keys(productData).find((key) => {
    if (typeof key === 'undefined') {
      console.log(productData);
      throw new Error('key is undefined');
    }

    if (typeof productData[key] === 'undefined') {
      console.log(productData);
      throw new Error(`productData[key] is undefined for key ${key}`)
    }


    return (productData[key].id == variant);  // ignore number vs string here
  });
  if (typeof sku === 'undefined') {
    console.log(`count not find variant ${variant} in product data:`);
    console.log(productData);
    return null;
  }
  return sku;
}

function getSkuList() {
  return Object.keys(productData);  // each key is an sku
}

async function getPriceString(sku) {
  await refreshProductData();  // typically returns immediately.
  return productData[sku].price_string;
}

function isOnCart() {
  const cartUrls = constants.shopifyDomainsByCurrency;
  const url = window.location.href;

  for (const currency in cartUrls) {
    const cartUrl = cartUrls[currency];
    if (url.includes(cartUrl)) {
      return true;
    }
  }

  return false;
}

function isOnCheckout() {
  if (isOnCart() === false) return false;

  const url = window.location.href;

  if (url.includes('/checkout')) return true;

  return false;
}

function getCurrencyFromCartUrl() {
  const cartUrls = constants.shopifyDomainsByCurrency;
  const url = window.location.href;
  
  if(window.location.host === constants.stagingShopifyHost) {
    return 'USD';
  }

  for (const currency in cartUrls) {
    const cartUrl = cartUrls[currency];
    if (url.includes(cartUrl)) {
      return currency;
    }  // upper case
  }

  return null;
}

function getDefaultCountryIsoByCurrency(currency) {
  return constants.defaultCountriesByCurrency[currency];  // iso, caps
}

function getAllCountries() {
  return constants.allCountries;
}

function getCurrencyByCountryIso(countryIso) {
  for (const currency in constants.countriesByCurrency) {
    const countryArray = constants.countriesByCurrency[currency];

    if (countryArray.includes(countryIso)) {
      return currency;  // upper case
    }
  }
  return 'USD';  // USD is the "catch all".
}

function getAccessoriesUrl() {
  const shopifyCurrencyStoreUrl =
      constants.shopifyDomainsByCurrency[state.getCurrency()];  // upper case -> lower case for url
  const accessoriesUrl =
      `${shopifyCurrencyStoreUrl}/collections/accessories`;

  return accessoriesUrl;
}

function getProductsUrl() {
  const shopifyCurrencyStoreUrl = 
      constants.shopifyDomainsByCurrency[state.getCurrency()];   // upper case -> lower case for url
  const productsUrl =
      `${shopifyCurrencyStoreUrl}/products/saleae-logic-pro-16`;

  return productsUrl;
}

function setCustomerInfo(customerId) {
  const vatState = state.getVatState();
  const currency = state.getCurrency();

  let vatExempt = false;


  let vatNumber = '';
  if (vatState !== null) {
    // do not mark any UK purchases as VAT-exempt
    if (currency == 'EUR') {
      vatExempt = true;
    }

    vatNumber = vatState.number;
  }

  // fire and forget
  server.setCustomerInfo(customerId, currency, vatExempt, vatNumber);
}

async function addItemToCart(sku) {
  await refreshProductData();
  window.location.href = productData[sku].url;
}

function navigateToCart() {
  // used when clicking the cart icon
  // also possibly called from currency.changeCountry

  const currency = state.getCurrency();  // upper case
  const url = constants.cartUrlsByCurrency[currency];
  window.location.href = url;
}

function navigateToStoreWithPath(pathname) {
  // Strip country from url and change page to country user selected

  const currency = state.getCurrency();  // upper case
  const base_url = constants.shopifyDomainsByCurrency[currency];
  window.location.href = base_url + pathname;
}

function updateCheckoutCountry() {
  const shipping_country =
      document.querySelector('#checkout_shipping_address_country');
  if (shipping_country === null) {
    return;
  }

  // let Shopify control the country, for consistency.
  // let selected_country_name = shipping_country.value; // "Italy"
  const selected_country_iso =
      shipping_country.options[shipping_country.selectedIndex].getAttribute(
          'data-code');

  if (selected_country_iso !== null) {
    state.setCountryIso(selected_country_iso);
  }

  // when the user changes country via Shopify, update our country as well.
  shipping_country.addEventListener('change', () => {
    const selected_country_iso =
        shipping_country.options[shipping_country.selectedIndex].getAttribute(
            'data-code');

    if (selected_country_iso !== null) {
      state.setCountryIso(selected_country_iso);
    }

    updateTaxDisplay();
  }, false);
}

function zeroTaxesUpdate() {
  const total_price =
      document.querySelector('[data-checkout-payment-due-target]');
  if (total_price === null) {
    return;
  }  // not on the checkout page

  let tax_line = document.querySelector('[data-checkout-total-taxes-target]');
  if (tax_line === null) return;
  // if (tax_line === null) { throw 'expect [data-checkout-total-taxes-target]
  // to be on this page'; }

  const vatSate = state.getVatState();
  if (vatSate === null) {
    return;
  }

  // note the currency is either GBP or EUR, so currency is displayed as
  // £346,80

  const current_currency = state.getCurrency();  // upper case
  if (current_currency !== 'EUR' && current_currency !== 'GBP') {
    throw `unexpected currency ${current_currency}`;
  }

  let currency_symbol = '£';
  if (current_currency === 'EUR') {
    currency_symbol = '€';
  }

  const current_tax_str =
      tax_line.getAttribute('data-checkout-total-taxes-target');
  const current_total_str =
      total_price.getAttribute('data-checkout-payment-due-target');

  const current_tax = Number.parseInt(current_tax_str);
  const current_total = Number.parseInt(current_total_str);

  if (isNaN(current_tax) || isNaN(current_total)) {
    throw `expected number strings to be integers ${current_tax_str}; ${
        current_total_str};`;
  }

  const new_tax_str = `${currency_symbol}0,00`;

  let new_total_str = (current_total - current_tax).toString();
  const len = new_total_str.length;
  new_total_str = `${currency_symbol + new_total_str.slice(0, len - 2)},${
      new_total_str.slice(len - 2)}`;

  tax_line.textContent = new_tax_str;

  // there are actually multiple of these on the page
  let total_prices =
      document.querySelectorAll('[data-checkout-payment-due-target]');
  total_prices.forEach((total) => {
    total.textContent = new_total_str;
  });

  // when showing billing info, the field is enabled
  const observer = new MutationObserver((() => {
    // console.log('mutation!');

    // elements will have been replaced
    tax_line = document.querySelector('[data-checkout-total-taxes-target]');
    total_prices =
        document.querySelectorAll('[data-checkout-payment-due-target]');

    tax_line.textContent = new_tax_str;

    total_prices.forEach((total) => {
      total.textContent = new_total_str;
    });
  }));
  observer.observe(
      tax_line.parentElement.parentElement.parentElement.parentElement
          .parentElement.parentElement,
      {childList: true});
}

function updateTaxDisplay() {
  // cart page
  const tax_cart_section = document.querySelector('.sal-tax');
  const tax_included = document.querySelector('.sal-tax-included');
  const tax_detail = document.querySelector('.sal-tax-detail');
  const total_section = document.querySelector('.cart__total-top');

  if (tax_included && tax_detail && tax_cart_section) {
    const tax_info = tax.getCartTaxMessage();
    const tax_popover = tax.getCartTaxMessagePopover();

    tax_included.textContent = tax_info;
    tax_detail.textContent = tax_popover;

    if (tax_info === null) {
      tax_cart_section.style.display = 'none';
      total_section.style.borderBottom = 'none';
    }
  }

  // for checkout pages:
  const tax_info_section = document.querySelector('.sal-checkout-tax-info');
  if (tax_info_section === null) {
    return;
  }

  let vat_number = '';
  let vat_company = '';
  const vatState = state.getVatState();

  if (vatState !== null) {
    vat_number = vatState.number;
    vat_company = vatState.company;

    if (state.getCurrency() === 'GBP') {
      tax_info_section.textContent =
          `Your VAT # ${vat_number} will appear on your invoice.`;
    } else {
      tax_info_section.textContent = `Claiming VAT exemption, ${vat_number}`;
    }

  } else {
    // normal tax info
    tax_info_section.textContent = tax.getCheckoutTaxMessage();
  }

  // Force the company name to the vat-exempt company, on checkout pages.

  if (vatState !== null) {
    const company_label =
        document.querySelector('div[data-address-field="company"] label');
    const company_input =
        document.querySelector('div[data-address-field="company"] input');

    if (company_label === null) {
      return;
    }

    company_label.textContent = 'Company';

    company_input.placeholder = 'Company';

    if (typeof vat_company === 'string' && vat_company.length > 0) {
      company_input.value = `${vat_company}, VAT # ${vat_number}`;
    }

    const company_div =
        document.querySelector('div[data-address-field="company"].field');
    company_div.classList.replace('field--optional', 'field--required');

    /// prevent form submission when a company is required (when there is a VAT
    /// number)
    const form = document.querySelector('form.edit_checkout');
    form.addEventListener('submit', (e) => {
      if (company_input.value == '') {
        company_div.classList.add('field--error');
        company_div.insertAdjacentHTML(
            'beforeend',
            '<p class="field__message field__message--error" id="error-for-company">Please enter your company name</p>');

        e.preventDefault();
        return false;
      }

      return true;
    });

    // company_input.disabled = true;

    // disabling the company field seems to prevent shopify from saving it
    // so we will just prevent editing brute force
    /// NO - NOT ALL VAT Responses include the company name
    /*
    company_input.addEventListener('change', () => {
        const companyName = `${vat_company}, VAT # ${vat_number}`;
        if (company_input.value !== companyName) { company_input.value =
    companyName; }
    }, false);
    */
  }
}

function preventPoBoxes() {
  const address_div =
      document.querySelector('div[data-address-field="address1"].field');
  const address1_input =
      document.querySelector('div[data-address-field="address1"] input');
  const form = document.querySelector('form.edit_checkout');

  if (address1_input === null || address_div === null || form === null) {
    return;
  }

  form.addEventListener('submit', (e) => {
    let address = address1_input.value.toLowerCase();

    if (address.includes('po box') || address.includes('p.o.') ||
        address.includes('pobox') || address.includes('postbus') ||
        address.includes('postfach') || address.includes('postale') ||
        address.includes('post ')) {
      address_div.classList.add('field--error');
      address_div.insertAdjacentHTML(
          'beforeend',
          '<p class="field__message field__message--error" id="error-for-company">Unfortunately we are unable to ship to PO Boxes</p>');

      e.preventDefault();
      return false;
    }

    return true;
  });
}

function warnOnTooManyCharacters() {
  var style = document.createElement('style');
  style.innerHTML = `
        .saleae-warning-box {
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
            padding: 8px;
            margin: 12px 0px;
            overflow: hidden;
            color: rgba(199, 116, 0,1);
            border: solid 1px rgba(199, 116, 0,1);
            border-radius: 3px;
            background-color: rgba(199, 116, 0,0.25);
        };
    `;

  // Get the first script tag
  var ref = document.querySelector('script');

  // Insert our new styles before the first script tag
  ref.parentNode.insertBefore(style, ref);

  const address_div =
      document.querySelector('div[data-address-field="address1"].field');
  const firstNameInput =
      document.querySelector('div[data-address-field="first_name"] input');
  const lastNameInput =
      document.querySelector('div[data-address-field="last_name"] input');
  const companyInput =
      document.querySelector('div[data-address-field="company"] input');
  const address1Input =
      document.querySelector('div[data-address-field="address1"] input');
  const address2Input =
      document.querySelector('div[data-address-field="address2"] input');
  const form = document.querySelector('form.edit_checkout');
  const shippingFields = document.querySelector('div[data-address-fields]');

  if (address1Input === null || address_div === null || form === null) {
    return;
  }

  let firstNameValue = '';
  let lastNameValue = '';
  let companyValue = '';
  let addressValue = '';
  let address2Value = '';

  const showWarning = (type, charCount = 35) => {
    let warning = document.querySelector(`div[data-warning="${type}"]`);
    if (warning !== null) {
      return;
    }
    warning = document.createElement('div');
    warning.innerHTML =
        `Looks like what you put for ${type} is over ${charCount} characters.
        This can sometimes slow down the shipping process as we verify a working shipping
        address.`
    warning.setAttribute('data-warning', type);
    warning.setAttribute('class', 'saleae-warning-box')
    shippingFields.prepend(warning);
  };
  const removeWarning = (type) => {
    const warning = document.querySelector(`div[data-warning="${type}"]`);
    if (warning === null) {
      return;
    }
    warning.remove();
  };

  const checkValues = () => {
    console.log('checking values', firstNameValue, companyValue, addressValue)
    if ((firstNameValue.length + lastNameValue.length + 1) > 35) {
      showWarning('name (first and last)')
    }
    else {
      removeWarning('name (first and last)')
    }
    if (companyValue.length > 35) {
      showWarning('company')
    } else {
      removeWarning('company')
    }
    if (addressValue.length > 35) {
      showWarning('address')
    } else {
      removeWarning('address')
    }
    if (address2Value.length > 35) {
      showWarning('address (second line)')
    } else {
      removeWarning('address (second line)')
    }
  };

  firstNameInput.addEventListener('keyup', () => {
    const value = firstNameInput.value;
    firstNameValue = value;
    checkValues();
  });
  lastNameInput.addEventListener('keyup', () => {
    const value = lastNameInput.value;
    lastNameValue = value;
    checkValues();
  });
  companyInput.addEventListener('keyup', () => {
    const value = companyInput.value;
    companyValue = value;
    checkValues();
  });
  address1Input.addEventListener('keyup', () => {
    const value = address1Input.value;
    addressValue = value;
    checkValues();
  });
  address2Input.addEventListener('keyup', () => {
    const value = address2Input.value;
    address2Value = value;
    checkValues();
  });
}

function updateCustomerTaxExemptStatus() {
  if (typeof sal_var_customer_id === 'undefined') {
    return;  // this only exists late in the checkout process.
  };

  // console.log(`updateCustomerTaxExemptStatus; ${sal_var_customer_id}`);

  setCustomerInfo(sal_var_customer_id);
}

function checkForCountryCurrencyMismatch() {
  if (helpers.isHomePage()) return;
  if (helpers.isBlog()) return;
  if (helpers.isSupport()) return;

  const cartCurrency = getCurrencyFromCartUrl();
  const stateCurrency = state.getCurrency();

  if (cartCurrency === stateCurrency) return;

  // the cart we are on doesn't match the country we think we are.

  const newCountry = getDefaultCountryIsoByCurrency(cartCurrency);
  country.changeCountry(newCountry);
}

function updateAccessoriesLink() {
  const url = getAccessoriesUrl();

  const links = document.querySelectorAll('.sal-accessories-link');
  links.forEach((link) => {
    link.href = url;
  });
}

function updateProductsLink() {
  const url = getProductsUrl();

  const links = document.querySelectorAll(['.sal-products-link', '.btn-cta']);
  links.forEach((link) => {
    link.href = url;
  });
}

function updateProductShipInfo() {
  const elements = {
    stock_statuses: document.querySelectorAll('.sal-stock-status'),
    ship_froms: document.querySelectorAll('.sal-ships-from'),
    ship_whens: document.querySelectorAll('.sal-can-ship-when'),
  };

  const count = elements.stock_statuses.length;

  if (count === 0) {
    return;
  }  // evidently not a cart/product page

  for (let i = 0; i < count; i++) {
    // data-sal-in-stock
    // data-sal-item-incoming
    // data-sal-item-incoming-date

    const stock_status = elements.stock_statuses[i];
    const ships_from = elements.ship_froms[i];
    const ships_when = elements.ship_whens[i];

    if (stock_status.dataset.salInStock === 'false') {
      stock_status.textContent = t('out-of-stock');

      if (stock_status.dataset.salItemIncoming === 'false') {
        ships_when.textContent = t('unknown-delivery');
      } else {
        const date = stock_status.dataset.salItemIncomingDate;
        ships_when.textContent = t('estimated-ship', {'date': date});
      }
    } else {
      stock_status.textContent = t('in-stock');

      ships_when.textContent = getInStockShipsWhen();
    };

    ships_from.textContent = getCartShipsFrom();
  }
}


function getRelevantShippingHolidays(countryIso) {
  const shippingHolidaysList = constants.shippingHolidays[countryIso];

  const today = new Date();

  let relevant_holidays = [];

  for (const date_string in shippingHolidaysList) {
    if (shippingHolidaysList.hasOwnProperty(date_string)) {
      const parts = date_string.split('-');
      const holiday_date = new Date(parts[0], parts[1] - 1, parts[2]);

      const holiday_name = shippingHolidaysList[date_string];

      let time_before_holiday = holiday_date.getTime() - today.getTime();
      let days_before_holiday =
          Math.ceil(time_before_holiday / (1000 * 3600 * 24));

      // relevant_holidays.push( { "date": holiday_date, "name": holiday_name }
      // );

      if (days_before_holiday >= -1 && days_before_holiday <= 3) {
        relevant_holidays.push({'date': holiday_date, 'name': holiday_name});
      }
    }
  }

  return relevant_holidays;
}

// https://stackoverflow.com/questions/23483718/turn-array-into-comma-separated-grammatically-correct-sentence
function arrayToSentence(arr) {
  if (arr.length === 1) return arr[0];

  if (arr.length === 2) return arr[0] + ' or ' + arr[1];

  var last = arr.pop();
  return arr.join(', ') + ', or ' + last;
}

function getFormattedShippingHolidayMessage(countryIso, relevantHolidays) {
  const options = {weekday: 'long', month: 'short', day: 'numeric'};

  const locale = countryIso === 'US' ? 'en-US' : 'en-GB';

  let formatted_holidays = [];

  relevantHolidays.forEach((holiday, index) => {
    const formated_day = holiday.date.toLocaleDateString(locale, options);
    formatted_holidays.push(formated_day + ' (' + holiday.name + ')');
  });

  const comma_list = arrayToSentence(formatted_holidays);

  return comma_list;
}

function addHolidayShippingSchedule() {
  let ship_from = document.querySelector('.sal-ships-from');

  if (ship_from == null) return;

  const currencyIso = getCurrencyFromCartUrl();  // upper case

  let countryIso = 'US';
  if (currencyIso === 'GBP') {
    countryIso = 'GB';
  }
  if (currencyIso === 'EUR') {
    countryIso = 'NL';
  }

  const relevant_holidays = getRelevantShippingHolidays(countryIso);

  if (relevant_holidays.length == 0) return;

  let shipping_holiday_message =
      getFormattedShippingHolidayMessage(countryIso, relevant_holidays);
  shipping_holiday_message =
      'Please note: items will not ship on ' + shipping_holiday_message + '.';

  let holiday_div = document.createElement('div');

  holiday_div.style = 'margin-top: 10px; color: #ff8383;'
  holiday_div.innerHTML = shipping_holiday_message;


  ship_from.parentNode.insertBefore(holiday_div, ship_from.nextSibling);
}

function getInStockShipsWhen() {
  // simple version:
  // if it is Monday - Thursday in UTC-8, -> Ships w/i 24 hours
  // if it is Friday before 3pm UTC-8 -> Ships no later than Monday.
  // otherwise -> Ships Monday.

  const date = new Date(Date.now());  // current time in UTC-8

  // console.log( getShipTimeByDate( new Date("May 3, 2018, 09:14:30 GMT-08:00")
  // ) ); console.log( getShipTimeByDate( new Date("May 4, 2018, 09:14:30
  // GMT-08:00") ) ); console.log( getShipTimeByDate( new Date("May 4, 2018,
  // 15:14:30 GMT-08:00") ) ); console.log( getShipTimeByDate( new Date("May 5,
  // 2018, 09:14:30 GMT-08:00") ) );

  return getShipTimeByDate(date);
}

function getShipTimeByDate(date) {
  const day = date.getDay();

  // Sunday - Saturday : 0 - 6
  if (day >= 1 && day <= 4)  // Monday - Thursday inclusive
    return t('ships-24');

  // 3pm UTC-8 cutoff = 11pm UTC;
  if (day == 5 && date.getUTCHours() < 23)
    return t('ships-no-later-than-monday')

    return t('ships-monday');
}

function getCartShipsFrom() {
  const currency = state.getCurrency();  // upper case

  if (currency === 'CAD') return t('ships-ca');
  if (currency === 'JPY') return t('ships-jp');
  if (currency === 'AUD') return t('ships-au');
  if (currency === 'CNY') return t('ships-cn');
  if (currency === 'TWD') return t('ships-tw');
  if (currency === 'GBP') return t('ships-uk');

  const country_iso = state.getCountryIso();  // upper case

  if (currency === 'USD') {
    if (country_iso === 'US') return t('ships-us');
    ;  // no need to specify

    return t('ships-default');
  }

  if (currency !== 'EUR') {
    throw `unknown currency ${currency}`;
  }

  // if (country_iso === 'DE') t('ships-de');
  // if (country_iso === 'IT') t('ships-it');
  // if (country_iso === 'FR') t('ships-fr');
  // if (country_iso === 'ES') t('ships-es');

  return t('ships-eu');
}

function getShopifyAssetUrl(file) {
  // saved in theme.liquid and checkout.liquid
  let enLangUrl = window.theme.langFile;  // ...en.default.json...
  return enLangUrl.replace('en.default.json', file);
}

export {
  init,
  isOnCart,
  isOnCheckout,
  getVariantBySku,
  getSkuByVariant,
  getSkuList,
  getPriceString,
  getCurrencyFromCartUrl,
  getDefaultCountryIsoByCurrency,
  getAllCountries,
  getCurrencyByCountryIso,
  getAccessoriesUrl,
  setCustomerInfo,
  addItemToCart,
  navigateToCart,
  navigateToStoreWithPath,
  getShopifyAssetUrl,
  getProductUrl,
};
